// A report model -> a printed sheet (or a PDF, which is the same thing).
//
// There is no PDF library in this app and this does not add one. Every browser
// that can run the desktop shell already has a PDF writer behind its own print
// dialog — "Destination: Save as PDF" — and it produces selectable text, real
// page breaks and repeated table headers, which a canvas-to-image PDF does not.
// The cost of jsPDF + autotable is ~350 KB in a bundle that is also shipped over
// the air to phones, for a page only a Super Admin ever opens.
//
// ── It prints from a hidden IFRAME, not a popup ────────────────────────────
// `window.open` is blocked by default in most browsers unless the click is
// traced straight through to it, and a blocked popup is silent — the operator
// presses Print and nothing whatever happens. An iframe needs no permission,
// cannot be blocked, and leaves the page they were working on untouched
// underneath the dialog.
//
// ⚠️ THE FRAME MUST OUTLIVE THE DIALOG. Removing it as soon as `print()` returns
// cancels the job in browsers where the call is not modal — the preview closes
// itself, or prints a blank sheet. It comes off on `afterprint`, with a long
// timer behind that for the browsers which never fire it.

import { groupSections } from './reportLayout';

// The marker the sheet is recognised by. ⚠️ AN IFRAME LOADS TWICE. Appending one
// to the document loads `about:blank` and fires `load` for it, and only then
// does the srcdoc content arrive with a second `load`. Printing on the first is
// a blank sheet — which reads as "the report is empty", not as a bug in the
// printer — so the handler waits until this element is actually in the frame.
const ROOT_ID = 'sli-report';

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// Deliberately plain and deliberately black-on-white. The app's theme is the
// customer's own server-driven colour scheme (see core/theme/buildTheme) and a
// dark one costs a full ink cartridge and reads worse than the default; a
// printed list is a document, not a screenshot of the app.
const CSS = `
  @page { size: A4 portrait; margin: 12mm 10mm 14mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font: 10.5px/1.4 "Segoe UI", Roboto, Arial, sans-serif;
    color: #111; background: #fff;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }

  /* ── The header band ──────────────────────────────────────────────────
     The figures read ACROSS, not down. As a label/value list they were five
     stacked rows that pushed the first table a third of the way down the page
     and still read as a form rather than a report header. */
  .brand { font-size: 8.5px; letter-spacing: .14em; text-transform: uppercase; color: #777; }
  h1 { font-size: 17px; margin: 1px 0 0; letter-spacing: -.01em; }
  /* A GRID, not a flex row. Content-sized items left the labels at five ragged
     positions — "ROOMS" sitting wherever the location name happened to end —
     which is what read as "not aligned". Equal tracks line the labels and their
     values into columns. */
  .figs {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(30mm, 1fr));
    gap: 5px 6mm; margin: 6px 0 0;
  }
  .fig { min-width: 0; }
  .fig .k {
    display: block; font-size: 7.5px; letter-spacing: .1em;
    text-transform: uppercase; color: #777;
  }
  .fig .v { display: block; font-size: 11.5px; font-weight: 700; }
  hr { border: 0; border-top: 1.5px solid #111; margin: 7px 0 9px; }

  section { margin: 0 0 6mm; }
  /* The heading must not be the last thing on a page — a room name stranded
     above the fold with its devices overleaf is the one break that makes a
     printed list misread. */
  h2 { font-size: 11.5px; margin: 0 0 1px; break-after: avoid; page-break-after: avoid; }
  .sub { font-size: 8.5px; color: #777; margin: 0 0 3px; break-after: avoid; page-break-after: avoid; }
  .n { color: #777; font-weight: 400; }
  .empty { font-size: 9.5px; color: #777; font-style: italic; margin: 0 0 4px; }

  /* Two count tables share a row — see ui/shared/reportLayout. */
  .pair { display: grid; grid-template-columns: 1fr 1fr; gap: 0 8mm; align-items: start; }
  .pair > section { margin-bottom: 5mm; }

  /* ⚠️ table-layout:fixed IS LOAD-BEARING, NOT STYLING. With the default auto,
     a declared width is a SUGGESTION: a long device name or a nowrap cell makes
     the table wider than the page and the right-hand column is simply clipped
     at the margin — which is how the Pieces column went missing off the side of
     the inventory sheet. Fixed makes the widths binding, and overflow-wrap
     below makes a long word break instead of push.
     (No backticks anywhere in here — this is inside a template literal.) */
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  /* Repeats the column headers on every page the table runs onto — without it
     page two of a long room is four unlabelled columns. */
  thead { display: table-header-group; }
  tr { break-inside: avoid; page-break-inside: avoid; }
  th, td {
    padding: 2.4px 3px; text-align: left; vertical-align: top;
    font-size: 9.5px; overflow-wrap: anywhere;
    border-bottom: .5px solid #dcdcdc;
  }
  /* Rules across, none down. A full grid boxes every cell and turns a list into
     a spreadsheet screenshot; the eye tracks a row perfectly well on the zebra
     alone, and the sheet stops competing with its own data. */
  /* ⚠️ NOT UPPERCASE, AND NOT nowrap. Uppercase plus letter-spacing costs a
     header about a third of its width, and "SR. NO." then no longer fitted its
     own column: with table-layout:fixed it overflowed into "Room Name", which
     is exactly the misalignment this reads as. Sentence case is narrower, and
     wrapping means a header can never push into its neighbour whatever the
     column is called. */
  th {
    font-size: 8.5px; font-weight: 700; color: #333;
    border-bottom: 1px solid #111;
  }
  td.num, th.num { text-align: right; }
  /* ⚠️ NO white-space:nowrap HERE. A unique number is normally 6 digits or a
     12-character MAC and fits easily — but with table-layout:fixed an
     unbreakable cell does not widen its column, it OVERFLOWS INTO THE NEXT ONE.
     A 20-character number drew straight over the Status cell and the status
     simply vanished: the same class of silent wrongness as the clipping this
     replaced. Wrapping to a second line loses nothing. */
  td.mono { font-family: "Cascadia Mono", Consolas, monospace; }
  tbody tr:nth-child(even) td { background: #f4f6f8; }
  /* Legible in grey too: the WORDS differ, the colour only speeds it up. */
  td.ok { color: #1b6b3a; }
  td.bad { color: #a1272b; }

  footer { margin-top: 6mm; font-size: 8px; color: #777; }
`;

/*
 * A cell's classes. `tone` is the report's own vocabulary, not the printer's —
 * "Online" / "Offline" / "—" belongs to core/admin/installedReport, and a
 * printer that matched those words would have to be taught every report's
 * words. It returns 'ok', 'bad' or nothing.
 */
const cellClass = (col, value) => [
  col.align === 'right' ? 'num' : '',
  col.mono ? 'mono' : '',
  (col.tone && col.tone(value)) || '',
].filter(Boolean).join(' ');

const tableHtml = (section) => {
  const cols = section.columns || [];
  /*
   * ⚠️ A `colgroup`, AND THE WIDTHS ARE PERCENTAGES. Two reasons, and both were
   * faults:
   *
   *   • widths declared on `th` are what `table-layout: fixed` reads, but only
   *     from the first row — a colgroup states them once and cannot be lost;
   *   • they used to be millimetres, which is a PAPER unit. The on-screen table
   *     had no widths at all and sized its columns by content, so the preview
   *     and the sheet had different proportions for the same data. A percentage
   *     is of whatever it is in — 190mm of A4, or the width of a dialog — so one
   *     set of numbers now lays out both.
   */
  const group = `<colgroup>${cols
    .map((c) => (c.width ? `<col style="width:${esc(c.width)}">` : '<col>'))
    .join('')}</colgroup>`;
  const head = cols.map((c) => {
    const cls = c.align === 'right' ? ' class="num"' : '';
    return `<th${cls}>${esc(c.label)}</th>`;
  }).join('');
  const body = (section.rows || []).map((r) => {
    const tds = cols.map((c) => {
      const cls = cellClass(c, r[c.key]);
      return `<td${cls ? ` class="${cls}"` : ''}>${esc(r[c.key])}</td>`;
    }).join('');
    return `<tr>${tds}</tr>`;
  }).join('');
  return `<table>${group}<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
};

const sectionHtml = (section) => {
  const sub = section.subtitle ? `<p class="sub">${esc(section.subtitle)}</p>` : '';
  const count = (section.rows || []).length;
  const heading = `<h2>${esc(section.title)}`
    + `${count ? ` <span class="n">${count}</span>` : ''}</h2>`;
  const body = count
    ? tableHtml(section)
    : `<p class="empty">${esc(section.empty || 'Nothing here.')}</p>`;
  return `<section>${heading}${sub}${body}</section>`;
};

const groupHtml = (group) => {
  const inner = group.sections.map(sectionHtml).join('');
  return group.compact ? `<div class="pair">${inner}</div>` : inner;
};

/** The whole sheet as a standalone HTML document. Exported for tests. */
export function reportToHtml(report) {
  const figs = (report.meta || [])
    .map((m) => `<div class="fig"><span class="k">${esc(m.label)}</span>`
      + `<span class="v">${esc(m.value)}</span></div>`)
    .join('');
  const brand = report.brand ? `<div class="brand">${esc(report.brand)}</div>` : '';
  const sections = groupSections(report.sections).map(groupHtml).join('');
  const footer = report.footer ? `<footer>${esc(report.footer)}</footer>` : '';

  // The id is not decoration — printReport waits for it. See ROOT_ID.
  return `<!doctype html><html><head><meta charset="utf-8">`
    + `<title>${esc(report.title)}</title><style>${CSS}</style></head>`
    + `<body><div id="${ROOT_ID}">`
    + `${brand}<h1>${esc(report.title)}</h1><div class="figs">${figs}</div><hr>`
    + `${sections}${footer}</div></body></html>`;
}

/**
 * Open the browser's print dialog on this report.
 *
 * Resolves once the dialog has been handed the document — NOT once anything has
 * been printed. Whether the operator saved a PDF, sent it to a printer or
 * cancelled is not observable from here, so nothing may be reported as printed.
 */
export function printReport(report) {
  const html = reportToHtml(report);
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('title', 'print');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;'
    + 'border:0;opacity:0;pointer-events:none;';

  return new Promise((resolve) => {
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      // A frame that has already been detached (a re-render, a closed page)
      // throws here rather than failing quietly.
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    };

    let printed = false;
    frame.onload = () => {
      const win = frame.contentWindow;
      // The about:blank load — wait for the real one. `printed` guards the
      // other direction: a browser that fires `load` again after printing must
      // not open a second dialog.
      if (printed) return;
      if (!win || !win.document || !win.document.getElementById(ROOT_ID)) return;
      printed = true;
      win.onafterprint = cleanup;
      // Two minutes, for the browsers that never fire afterprint (older WebKit)
      // and for an operator who leaves the dialog open while they find a
      // printer. Far longer than any print takes, and the frame is 1px and
      // inert until then.
      setTimeout(cleanup, 120000);
      try {
        win.focus();
        win.print();
      } catch (e) {
        cleanup();
        resolve(false);
        return;
      }
      resolve(true);
    };

    // A frame that never loads would otherwise leave this promise — and the
    // button's spinner, if a caller ever adds one — pending for ever.
    setTimeout(() => {
      if (printed) return;
      cleanup();
      resolve(false);
    }, 15000);

    // srcdoc rather than document.write: it fires `load` reliably, which is what
    // guarantees the stylesheet is applied before print() is called. Printing an
    // unstyled document is the failure this ordering exists to prevent. Set
    // BEFORE the frame is connected, so the content load is the first one the
    // browser has to do rather than a second pass over about:blank.
    frame.srcdoc = html;
    document.body.appendChild(frame);
  });
}

export default printReport;
